/**
 * /payroll-l4/certified-payroll-reports/{reportId} — WH-347 detail page.
 *
 * Shows a header summary, the per-employee day-of-week hour grid, and the
 * fringe breakdown for the Statement of Compliance §4(a). From here a user
 * can sign the report, supersede it to issue a correction, record/email a
 * submission to an agency, regenerate it, and export it (PDF, LCPtracker,
 * CA DIR eCPR, eMars). The lifecycle actions are gated by report status.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import GestureIcon from "@mui/icons-material/Gesture";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RestoreIcon from "@mui/icons-material/Restore";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import { useState } from "react";
import { Menu, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { api, errorMessage, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";
import type {
  SoCPreflight,
  SubmissionMethod,
  WH347ReportFull,
  WH347Status,
} from "@/lib/types";

export const Route = createFileRoute(
  "/_auth/payroll-l4/certified-payroll-reports_/$reportId",
)({
  component: WH347DetailPage,
});

const STATUS_COLOR: Record<WH347Status, "default" | "success" | "warning" | "error" | "info"> = {
  draft: "default",
  generated: "info",
  signed: "success",
  submitted: "info",
  accepted: "success",
  rejected: "error",
  superseded: "warning",
};

function WH347DetailPage() {
  const { reportId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [signOpen, setSignOpen] = useState(false);
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "wh347", reportId],
    queryFn: async () =>
      (await api(`/api/payroll-l4/certified-payroll-reports/${reportId}`)) as WH347ReportFull,
  });

  const regenerate = useMutation({
    mutationFn: async () =>
      (await api(`/api/payroll-l4/certified-payroll-reports/generate`, {
        method: "POST",
        body: {
          project_id: report!.project_id,
          week_ending: report!.week_ending,
        },
      })) as WH347ReportFull,
    onSuccess: (rep) => {
      qc.setQueryData(["payroll-l4", "wh347", reportId], rep);
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
      toast.success("Report regenerated.");
    },
    onError: (e: unknown) => toast.error(errorMessage(e)),
  });

  const unsign = useMutation({
    mutationFn: async () =>
      (await api(`/api/payroll-l4/certified-payroll-reports/${reportId}/unsign`, {
        method: "POST", body: {},
      })) as WH347ReportFull,
    onSuccess: (rep) => {
      qc.setQueryData(["payroll-l4", "wh347", reportId], rep);
      qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
      toast.success("Report unsigned (admin recovery).");
    },
    onError: (e: unknown) => toast.error(errorMessage(e)),
  });

  const downloadExport = async (kind: "pdf" | "lcptracker" | "dir" | "emars") => {
    if (!report) return;
    const url = kind === "pdf"
      ? `/api/payroll-l4/certified-payroll-reports/${report.id}/pdf`
      : `/api/payroll-l4/certified-payroll-reports/${report.id}/export/${kind}`;
    const ext = kind === "pdf" ? "pdf" : kind === "emars" ? "csv" : "xml";
    const stem = kind === "pdf" ? "WH-347"
                 : kind === "lcptracker" ? "LCPtracker"
                 : kind === "dir" ? "CA-DIR-eCPR" : "eMars";
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${stem}_${report.project_code ?? "report"}_${report.week_ending}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(`${stem} download failed: ${errorMessage(e)}`);
    }
  };

  const downloadPdf = async () => {
    if (!report) return;
    try {
      const r = await fetch(`/api/payroll-l4/certified-payroll-reports/${report.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WH-347_${report.project_code ?? "report"}_${report.week_ending}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`PDF download failed: ${errorMessage(e)}`);
    }
  };

  if (isLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{errorMessage(error)}</Alert></Box>;
  if (!report) return null;

  const immutable = ["signed", "submitted", "accepted", "rejected"].includes(report.status);
  const canSign = ["draft", "generated"].includes(report.status);
  const canSupersede = ["signed", "submitted", "accepted", "rejected"].includes(report.status);
  const canSubmit = ["signed"].includes(report.status);
  const canEmailWH347 = ["signed", "submitted", "accepted"].includes(report.status);

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button
          component={Link}
          to={"/payroll-l4/certified-payroll-reports" as never}
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          Back to Reports
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h4" fontWeight={700}>
              {report.project_code ?? report.project_id.slice(0, 8)}
            </Typography>
            <Typography variant="h5" color="text.secondary">
              · WH-347 #{report.payroll_number}
            </Typography>
            <Chip size="small" label={report.status} color={STATUS_COLOR[report.status]} />
            {report.is_final && <Chip size="small" label="FINAL" color="primary" />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {report.project_name} · week ending {report.week_ending}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint
              docPath="payroll-l4/step-6-wh347-pdf.md"
              label="WH-347 generation help"
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<GestureIcon />}
            color="success"
            onClick={() => setSignOpen(true)}
            disabled={!canSign}
            title={
              canSign
                ? "Sign the Statement of Compliance."
                : `You can sign once the report is generated. This report is ${report.status}.`
            }
          >
            Sign
          </Button>
          {report.status === "signed" && (
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={() => unsign.mutate()}
              disabled={unsign.isPending}
              title="Revert signature (admin) — clears the signature and returns the report to generated."
            >
              Unsign
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<HistoryEduIcon />}
            onClick={() => setSupersedeOpen(true)}
            disabled={!canSupersede}
            title={
              canSupersede
                ? "Issue a correcting report."
                : "Supersede is available after the report is signed — it issues a correcting copy."
            }
          >
            Supersede
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            color="primary"
            onClick={() => setSubmitOpen(true)}
            disabled={!canSubmit}
            title={
              canSubmit
                ? "Record a submission to an agency."
                : "Sign the report before submitting it to an agency."
            }
          >
            Record submission
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={() => setEmailOpen(true)}
            disabled={!canEmailWH347}
            title={
              canEmailWH347
                ? "Email the WH-347 to a contracting officer."
                : "Sign the report before emailing it to a contracting officer."
            }
          >
            Email WH-347
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => regenerate.mutate()}
            disabled={immutable || regenerate.isPending}
            title={immutable ? "Signed/submitted reports must be superseded before regenerating" : ""}
          >
            {regenerate.isPending ? <CircularProgress size={18} /> : "Regenerate"}
          </Button>
          <Tooltip title="PDF for manual filing · CA DIR eCPR for the California state portal · LCPtracker / eMars for those reporting systems.">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
            >
              Export ▾
            </Button>
          </Tooltip>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => { setExportAnchor(null); downloadExport("pdf"); }}>
              WH-347 PDF
            </MenuItem>
            <MenuItem onClick={() => { setExportAnchor(null); downloadExport("lcptracker"); }}>
              LCPtracker XML
            </MenuItem>
            <MenuItem onClick={() => { setExportAnchor(null); downloadExport("dir"); }}>
              CA DIR eCPR XML
            </MenuItem>
            <MenuItem onClick={() => { setExportAnchor(null); downloadExport("emars"); }}>
              eMars CSV
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Summary card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={4}>
          <Stat label="Workers" value={String(report.total_employees)} />
          <Stat label="Total hours" value={report.total_hours} />
          <Stat label="Gross (this project)" value={`$${report.total_gross_wages}`} />
          <Stat label="Generated" value={report.generated_at?.slice(0, 19).replace("T", " ") ?? "—"} />
          <Stat label="Signed" value={report.signed_at?.slice(0, 19).replace("T", " ") ?? "(unsigned)"} />
        </Stack>
      </Paper>

      {/* Lines */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
        Payroll lines ({report.lines.length})
      </Typography>
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Class.</TableCell>
              <TableCell align="right">Mon</TableCell>
              <TableCell align="right">Tue</TableCell>
              <TableCell align="right">Wed</TableCell>
              <TableCell align="right">Thu</TableCell>
              <TableCell align="right">Fri</TableCell>
              <TableCell align="right">Sat</TableCell>
              <TableCell align="right">Sun</TableCell>
              <TableCell align="right">
                <Tooltip title="ST = straight time (regular hours)">
                  <span>ST</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="OT = overtime hours">
                  <span>OT</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Gross (proj)</TableCell>
              <TableCell align="right">Net</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  <strong>{line.worker_name}</strong>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    SSN ***-**-{line.ssn_last_4}
                    {line.is_apprentice && " · APPRENTICE"}
                  </Typography>
                </TableCell>
                <TableCell>{line.wd_classification_name}</TableCell>
                {(["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const).map((d) => {
                  const st = (line as unknown as Record<string, string>)[`hours_${d}_st`];
                  const ot = (line as unknown as Record<string, string>)[`hours_${d}_ot`];
                  return (
                    <TableCell key={d} align="right">
                      {Number(st).toFixed(2)}
                      {Number(ot) > 0 && (
                        <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
                          OT {Number(ot).toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="right">{line.total_hours_st}</TableCell>
                <TableCell align="right">{line.total_hours_ot}</TableCell>
                <TableCell align="right">${line.base_rate}</TableCell>
                <TableCell align="right">${line.gross_wages_this_project}</TableCell>
                <TableCell align="right">${line.net_wages}</TableCell>
              </TableRow>
            ))}
            {report.lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No labor events on this project for this week.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Fringe breakdown */}
      {report.fringe_breakdown.length > 0 && (
        <>
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 3, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Fringe breakdown for SoC §4(a)
            </Typography>
            <Tooltip title="§4(a) = bona-fide fringe benefits (health, pension, training). §4(b) = paid as cash. Both count toward the prevailing wage.">
              <Box component="span" sx={{ display: "inline-flex", color: "text.secondary", cursor: "help" }}>
                <HelpOutlineIcon fontSize="small" />
              </Box>
            </Tooltip>
          </Stack>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fund</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Total this week</TableCell>
                  <TableCell>Bona-fide §4(a)?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.fringe_breakdown.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>{f.fund_name_snapshot}</TableCell>
                    <TableCell>{f.fund_type_code ?? "—"}</TableCell>
                    <TableCell align="right">${f.total_amount}</TableCell>
                    <TableCell>
                      {f.is_bona_fide_4a ? (
                        <Chip size="small" color="success" label="Yes — §4(a)" />
                      ) : (
                        <Chip size="small" label="No — treated as §4(b) cash" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Lifecycle dialogs */}
      <SignDialog
        open={signOpen}
        onClose={() => setSignOpen(false)}
        reportId={reportId}
        onSigned={(rep) => {
          qc.setQueryData(["payroll-l4", "wh347", reportId], rep);
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          setSignOpen(false);
          toast.success("Statement of Compliance signed.");
        }}
      />
      <SupersedeDialog
        open={supersedeOpen}
        onClose={() => setSupersedeOpen(false)}
        reportId={reportId}
        onSuperseded={(newRep) => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          setSupersedeOpen(false);
          toast.success(
            `Superseded — opening new report #${newRep.payroll_number}.`,
          );
          navigate({
            to: "/payroll-l4/certified-payroll-reports/$reportId" as never,
            params: { reportId: newRep.id } as never,
          });
        }}
      />
      <SubmitDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        reportId={reportId}
        onSubmitted={() => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          qc.invalidateQueries({ queryKey: ["payroll-l4", "submissions"] });
          setSubmitOpen(false);
          toast.success("Submission recorded; report status → submitted.");
        }}
      />
      <EmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportId={reportId}
        onSent={(toEmail) => {
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          setEmailOpen(false);
          toast.success(`WH-347 emailed to ${toEmail}`);
        }}
      />
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// SignDialog — Step 8 + Step 14 SoC pre-flight narrative
// ─────────────────────────────────────────────────────────────────────────
function SignDialog({
  open, onClose, reportId, onSigned,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  onSigned: (rep: WH347ReportFull) => void;
}) {
  const [signatureText, setSignatureText] = useState("");

  // Fetch the AI pre-flight summary lazily once the dialog opens
  const preflight = useQuery({
    queryKey: ["payroll-l4", "wh347", reportId, "preflight"],
    queryFn: async () =>
      (await api(
        `/api/payroll-l4/certified-payroll-reports/${reportId}/ai/soc-preflight`,
        { method: "POST", body: {} },
      )) as SoCPreflight,
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });

  const sign = useMutation({
    mutationFn: async () =>
      (await api(
        `/api/payroll-l4/certified-payroll-reports/${reportId}/sign`,
        { method: "POST", body: { signature_text: signatureText.trim() } },
      )) as WH347ReportFull,
    onSuccess: (rep) => {
      setSignatureText("");
      onSigned(rep);
    },
  });

  const goNoGoColor: Record<SoCPreflight["go_no_go"], "success" | "warning" | "error"> = {
    go: "success",
    review: "warning",
    stop: "error",
  };

  return (
    <Dialog
      open={open}
      onClose={() => !sign.isPending && (setSignatureText(""), onClose())}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Sign Statement of Compliance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Type your full name to sign the WH-347 Statement of Compliance.
          Captures IP + browser + timestamp; the signed report is immutable
          (only Supersede can issue corrections).
        </Typography>

        {preflight.isLoading && (
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Bynaus is reviewing the report before signing…
            </Typography>
          </Stack>
        )}
        {preflight.isError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn't run the pre-sign review: {errorMessage(preflight.error)}.
            You can still sign, or close and try again.
          </Alert>
        )}
        {preflight.data && (
          <Alert
            severity={goNoGoColor[preflight.data.go_no_go]}
            sx={{ mb: 2 }}
            icon={false}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {preflight.data.headline}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {preflight.data.narrative}
            </Typography>
            {preflight.data.concerns.length > 0 && (
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                {preflight.data.concerns.map((c, i) => (
                  <li key={i} style={{ fontSize: 12 }}>{c}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Type your full name"
          value={signatureText}
          onChange={(e) => setSignatureText(e.target.value)}
          disabled={sign.isPending}
          placeholder="e.g. Jane Q. Contractor"
        />
        {sign.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(sign.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setSignatureText(""); onClose(); }} disabled={sign.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={preflight.data?.go_no_go === "stop" ? "warning" : "primary"}
          disabled={!signatureText.trim() || sign.isPending}
          onClick={() => sign.mutate()}
        >
          {sign.isPending ? <CircularProgress size={18} /> : "Sign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// SupersedeDialog — Step 9
// ─────────────────────────────────────────────────────────────────────────
function SupersedeDialog({
  open, onClose, reportId, onSuperseded,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  onSuperseded: (newRep: WH347ReportFull) => void;
}) {
  const [notes, setNotes] = useState("");
  const supersede = useMutation({
    mutationFn: async () =>
      (await api(
        `/api/payroll-l4/certified-payroll-reports/${reportId}/supersede`,
        { method: "POST", body: { notes: notes.trim() || null } },
      )) as WH347ReportFull,
    onSuccess: (rep) => {
      setNotes("");
      onSuperseded(rep);
    },
  });
  return (
    <Dialog
      open={open}
      onClose={() => !supersede.isPending && (setNotes(""), onClose())}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Supersede this report</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Issues a new <strong>draft</strong> at the same project + week, with
          a fresh sequential payroll number. The current report is flipped to
          <strong> superseded</strong> and its <code>superseded_by_id</code>
          points at the new one. Sign + submit the new report when ready.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          size="small"
          label="Notes (saved on both records)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={supersede.isPending}
          placeholder="e.g. Corrected worker classification on Carpenter line."
        />
        {supersede.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(supersede.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setNotes(""); onClose(); }} disabled={supersede.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={supersede.isPending}
          onClick={() => supersede.mutate()}
        >
          {supersede.isPending ? <CircularProgress size={18} /> : "Supersede"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// SubmitDialog — Step 12, record a portal submission
// ─────────────────────────────────────────────────────────────────────────
function SubmitDialog({
  open, onClose, reportId, onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  onSubmitted: () => void;
}) {
  const [method, setMethod] = useState<SubmissionMethod>("lcptracker");
  const [submittedTo, setSubmittedTo] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [notes, setNotes] = useState("");

  const submit = useMutation({
    mutationFn: async () =>
      await api(
        `/api/payroll-l4/reports/${reportId}/submissions`,
        {
          method: "POST",
          body: {
            submission_method: method,
            submitted_to: submittedTo.trim() || null,
            confirmation_reference: confirmation.trim() || null,
            notes: notes.trim() || null,
          },
        },
      ),
    onSuccess: () => {
      setSubmittedTo(""); setConfirmation(""); setNotes("");
      onSubmitted();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => !submit.isPending && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Record submission</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Logs that you submitted this report to a portal / recipient. Flips
          the report to <strong>submitted</strong>; record the response
          (accepted / rejected) on the Submission Log when it comes back.
        </Typography>
        <Stack spacing={2}>
          <TextField
            select fullWidth size="small" label="Method" value={method}
            onChange={(e) => setMethod(e.target.value as SubmissionMethod)}
            disabled={submit.isPending}
          >
            <MenuItem value="lcptracker">LCPtracker</MenuItem>
            <MenuItem value="dir_ecpr">CA DIR eCPR</MenuItem>
            <MenuItem value="emars">eMars</MenuItem>
            <MenuItem value="pdf_email">PDF email</MenuItem>
            <MenuItem value="manual_portal">Manual portal upload</MenuItem>
          </TextField>
          <TextField
            fullWidth size="small" label="Submitted to (email / portal name)"
            value={submittedTo}
            onChange={(e) => setSubmittedTo(e.target.value)}
            disabled={submit.isPending}
          />
          <TextField
            fullWidth size="small" label="Confirmation reference"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            disabled={submit.isPending}
            placeholder="LCP-2024-001 / message-id / ack code"
          />
          <TextField
            fullWidth multiline rows={2} size="small" label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submit.isPending}
          />
        </Stack>
        {submit.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(submit.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submit.isPending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? <CircularProgress size={18} /> : "Record"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// EmailDialog — email WH-347 PDF to contracting officer
// ─────────────────────────────────────────────────────────────────────────
function EmailDialog({
  open, onClose, reportId, onSent,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  onSent: (toEmail: string) => void;
}) {
  const [toEmail, setToEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [notes, setNotes] = useState("");

  const sendEmail = useMutation({
    mutationFn: async () =>
      await api(
        `/api/payroll-l4/certified-payroll-reports/${reportId}/submit-email`,
        {
          method: "POST",
          body: {
            to_email: toEmail.trim(),
            cc_email: ccEmail.trim() || null,
            notes: notes.trim() || null,
          },
        },
      ),
    onSuccess: () => {
      const sent = toEmail.trim();
      setToEmail(""); setCcEmail(""); setNotes("");
      onSent(sent);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => !sendEmail.isPending && (setToEmail(""), setCcEmail(""), setNotes(""), onClose())}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Email WH-347</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Renders the WH-347 PDF and emails it to the contracting officer.
          A submission record will be created automatically.
        </Typography>
        <Stack spacing={2}>
          <TextField
            autoFocus
            fullWidth
            required
            size="small"
            label="To (contracting officer email)"
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            disabled={sendEmail.isPending}
          />
          <TextField
            fullWidth
            size="small"
            label="CC (optional)"
            type="email"
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
            disabled={sendEmail.isPending}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Notes (optional — included in email body)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={sendEmail.isPending}
          />
        </Stack>
        {sendEmail.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(sendEmail.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => { setToEmail(""); setCcEmail(""); setNotes(""); onClose(); }}
          disabled={sendEmail.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={sendEmail.isPending ? <CircularProgress size={16} /> : <EmailIcon />}
          disabled={!toEmail.trim() || sendEmail.isPending}
          onClick={() => sendEmail.mutate()}
        >
          {sendEmail.isPending ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontSize: 10 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>{value}</Typography>
    </Box>
  );
}
